import { useId, useLayoutEffect, useRef, useState, type CSSProperties, type InputHTMLAttributes, type ReactNode } from 'react';
import { Check, ChevronDown, Eye, EyeOff, Minus, Search, X } from 'lucide-react';
import { Input, type InputProps } from '@/components/ui/primitives';
import { useEscape } from '@/hooks/useEscape';
import { useMountTransition } from '@/hooks/useMountTransition';
import { useOutsideClick } from '@/hooks/useOutsideClick';

/**
 * Form controls.
 *
 * Native `<select>` and `<input type=checkbox>` are replaced here, not for
 * decoration: the native ones cannot be styled consistently across Windows,
 * macOS and Linux, so a panel that uses them looks like three different
 * applications depending on who opened it. What is kept is their behaviour —
 * keyboard operation, labels, roles — because that is the part users rely on.
 */

export interface SelectOption<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly hint?: string;
  readonly disabled?: boolean;
}

export interface CustomSelectProps<T extends string> {
  readonly value: T;
  readonly options: readonly SelectOption<T>[];
  readonly onChange: (value: T) => void;
  readonly label: string;
  readonly id?: string;
  readonly size?: 'sm' | 'md';
  readonly disabled?: boolean;
  readonly placeholder?: string;
}

/** The tallest a list of options gets, and how close it may come to the window's edge. */
const LIST_MAX = 280;
const EDGE = 8;

/**
 * Where an open list goes: under its field, or over it when there is more
 * room above — always inside the window, whatever scrolls around the field.
 * Fixed rather than absolute, so a dialog's or a card's edge does not cut it.
 */
function useListPlacement(open: boolean, anchor: { current: HTMLElement | null }, list: { current: HTMLElement | null }): CSSProperties | undefined {
  const [place, setPlace] = useState<CSSProperties>();

  useLayoutEffect(() => {
    if (!open) return;
    const update = (): void => {
      const box = anchor.current?.getBoundingClientRect();
      if (!box) return;
      const below = window.innerHeight - box.bottom - EDGE;
      const above = box.top - EDGE;
      const wanted = Math.min(LIST_MAX, list.current?.scrollHeight ?? LIST_MAX);
      const up = below < wanted && above > below;
      const width = Math.max(box.width, list.current?.offsetWidth ?? 0);
      setPlace({
        position: 'fixed',
        left: Math.max(EDGE, Math.min(box.left, window.innerWidth - width - EDGE)),
        minWidth: box.width,
        maxHeight: Math.max(120, Math.min(LIST_MAX, (up ? above : below) - 4)),
        ...(up ? { top: 'auto', bottom: window.innerHeight - box.top + 4 } : { top: box.bottom + 4, bottom: 'auto' }),
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchor, list]);

  return open ? place : undefined;
}

export function Select<T extends string>({
  value,
  options,
  onChange,
  label,
  id,
  size = 'md',
  disabled = false,
  placeholder = 'Выберите…',
}: CustomSelectProps<T>): ReactNode {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const wrapper = useRef<HTMLDivElement | null>(null);
  const list = useRef<HTMLDivElement | null>(null);
  const generatedId = useId();
  const listId = `${id ?? generatedId}-listbox`;

  const { mounted, phase } = useMountTransition(open, 140);
  const place = useListPlacement(mounted, wrapper, list);
  useEscape(open, () => setOpen(false));
  useOutsideClick(open, wrapper, () => setOpen(false));

  const selected = options.find((option) => option.value === value);
  const enabled = options.filter((option) => !option.disabled);

  const choose = (option: SelectOption<T>): void => {
    onChange(option.value);
    setOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (disabled) return;

    if (!open && ['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      setOpen(true);
      setHighlighted(Math.max(0, enabled.findIndex((option) => option.value === value)));
      return;
    }

    if (!open) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setHighlighted((current) => (current + step + enabled.length) % enabled.length);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const option = enabled[highlighted];
      if (option) choose(option);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setHighlighted(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setHighlighted(enabled.length - 1);
    }
  };

  return (
    <div className="select-wrap" ref={wrapper}>
      <button
        type="button"
        id={id ?? generatedId}
        className={`select-trigger ${size === 'sm' ? 'select-trigger-sm' : ''}`}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        aria-label={label}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={onKeyDown}
      >
        <span className={selected ? '' : 'muted'}>{selected?.label ?? placeholder}</span>
        <ChevronDown size={15} aria-hidden="true" className={open ? 'select-caret-open' : 'select-caret'} />
      </button>

      {mounted ? (
        <div ref={list} className={`select-list popover-${phase}`} style={place} role="listbox" id={listId} aria-label={label}>
          {options.map((option) => {
            const index = enabled.indexOf(option);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                disabled={option.disabled ?? false}
                className={`select-option ${index === highlighted && index >= 0 ? 'select-option-active' : ''}`}
                onMouseEnter={() => index >= 0 && setHighlighted(index)}
                onClick={() => choose(option)}
              >
                <span className="select-option-text">
                  {option.label}
                  {option.hint ? <span className="select-option-hint">{option.hint}</span> : null}
                </span>
                {option.value === value ? <Check size={15} aria-hidden="true" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export interface CheckboxProps {
  readonly checked: boolean;
  /** Neither on nor off: some of the things below this are selected. */
  readonly indeterminate?: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly label: ReactNode;
  readonly hint?: string;
  readonly disabled?: boolean;
}

export function Checkbox({
  checked,
  indeterminate = false,
  onChange,
  label,
  hint,
  disabled = false,
}: CheckboxProps): ReactNode {
  return (
    <label className={`checkbox ${disabled ? 'checkbox-disabled' : ''}`}>
      {/* The real input stays, visually hidden: it carries the role, the
          keyboard behaviour and the form semantics. The box beside it is only
          paint. */}
      <input
        type="checkbox"
        className="checkbox-input"
        checked={checked}
        disabled={disabled}
        aria-checked={indeterminate ? 'mixed' : checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="checkbox-box" aria-hidden="true">
        {indeterminate ? <Minus size={12} strokeWidth={3} /> : checked ? <Check size={12} strokeWidth={3} /> : null}
      </span>
      <span className="checkbox-text">
        {label}
        {hint ? <span className="field-hint">{hint}</span> : null}
      </span>
    </label>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly label: string;
  readonly disabled?: boolean;
}): ReactNode {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className="switch"
      onClick={() => onChange(!checked)}
    >
      <span className="switch-thumb" aria-hidden="true" />
    </button>
  );
}

/** A password field with a reveal toggle, because typing 20 characters blind
 *  into a confirmation box is how people end up locked out. */
export function PasswordInput(props: InputProps): ReactNode {
  const [visible, setVisible] = useState(false);

  return (
    <div className="input-affix">
      <Input {...props} type={visible ? 'text' : 'password'} />
      <button
        type="button"
        className="input-affix-button"
        aria-label={visible ? 'Скрыть пароль' : 'Показать пароль'}
        title={visible ? 'Скрыть пароль' : 'Показать пароль'}
        onClick={() => setVisible((current) => !current)}
      >
        {visible ? <EyeOff size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}
      </button>
    </div>
  );
}

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly label: string;
}

export function SearchInput({ value, onChange, label, ...rest }: SearchInputProps): ReactNode {
  return (
    <div className="input-affix input-affix-leading">
      <Search size={15} aria-hidden="true" className="input-affix-icon" />
      <Input {...rest} aria-label={label} value={value} onChange={(event) => onChange(event.target.value)} />
      {value.length > 0 ? (
        <button
          type="button"
          className="input-affix-button"
          aria-label="Очистить"
          onClick={() => onChange('')}
        >
          <X size={15} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
